/**
 * Single source of truth for the site's static (non-dynamic) routes.
 *
 * Consumed by:
 *   - scripts/generate-sitemap.ts        — SITEMAP_STATIC_ROUTES become sitemap-static.xml
 *   - scripts/refresh-lastmod-manifest.ts — sourceFile list for <lastmod> git dates
 *   - server/static.ts                   — isKnownStaticPath() lets the SSR catchall
 *                                          return a real HTTP 404 for unknown URLs
 *                                          instead of a 200 soft-404
 *   - server/page-meta.ts                — unknown paths get robots noindex
 *
 * Adding a page? Put it in SITEMAP_STATIC_ROUTES if it should be indexed,
 * or NON_SITEMAP_STATIC_ROUTES if it's routable but shouldn't be in the
 * sitemap (stubs, slide decks, transactional pages, internal tools).
 * A real route missing from BOTH lists will 404 in production.
 */

import { INSTRUCTORS } from "./instructors";

export interface StaticRoute {
  path: string;
  priority: number;
  changefreq: string;
  // The page-component source file. Its last-commit date becomes the URL's
  // <lastmod>. Required so a route added without a sourceFile fails the type
  // check instead of silently regressing to "today".
  sourceFile: string;
}

// ─── Sitemap-bound, indexable routes ─────────────────────────────────────────
// Priority: 1.0 = home, 0.8 = top-level pages, 0.6 = blog/case-study index,
//           0.5 = deeper pages

export const SITEMAP_STATIC_ROUTES: StaticRoute[] = [
  // Core
  { path: "/",         priority: 1.0, changefreq: "weekly",  sourceFile: "client/src/pages/home.tsx" },
  { path: "/about-us", priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/about-us.tsx" },

  // Instructor bio pages (2026-10-13). One per person in shared/instructors.ts;
  // the record file is the sourceFile because that is what changes per person.
  { path: "/instructors", priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/instructors/instructors-index.tsx" },
  ...INSTRUCTORS.map((i) => ({
    path: `/instructors/${i.slug}`,
    priority: 0.7,
    changefreq: "monthly",
    sourceFile: "shared/instructors.ts",
  })),
  { path: "/vip-session", priority: 0.6, changefreq: "monthly", sourceFile: "client/src/pages/vip-session.tsx" },
  { path: "/nyf", priority: 0.7, changefreq: "weekly", sourceFile: "client/src/pages/nyf-workshop.tsx" },
  { path: "/contact",  priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/contact.tsx" },
  { path: "/careers",  priority: 0.7, changefreq: "weekly",  sourceFile: "client/src/pages/careers.tsx" },

  // AdVenture Plus membership (public marketing page). Legacy /pro and /plus 301 here.
  { path: "/membership", priority: 0.9, changefreq: "weekly", sourceFile: "client/src/pages/membership.tsx" },
  // The AI Context Toolkit, the free lead magnet (was /second-brain, which 301s here).
  { path: "/ai-context-toolkit", priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/ai-context-toolkit.tsx" },
  // The first month of Plus, free to watch (Oct 26 2026): the public lead page for list emails and ads.
  { path: "/membership/first-month", priority: 0.8, changefreq: "weekly", sourceFile: "client/src/pages/membership-first-month.tsx" },

  // Services hub
  { path: "/services", priority: 0.9, changefreq: "monthly", sourceFile: "client/src/pages/services/index.tsx" },

  // Service pages
  { path: "/google-ads-management",                   priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/google-ads-management.tsx" },
  { path: "/meta-ads-management",                     priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/meta-ads-management.tsx" },
  { path: "/landing-page-ab-testing",                 priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/landing-page-ab-testing.tsx" },
  { path: "/google-shopping-management",              priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/google-shopping-management.tsx" },
  { path: "/display-ads",                             priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/display-ads.tsx" },
  { path: "/retargeting",                             priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/retargeting.tsx" },
  { path: "/creative-services",                       priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/creative-services.tsx" },
  { path: "/seo-agency-services",                     priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/seo-agency-services.tsx" },
  { path: "/answer-engine-optimization-aeo-services", priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/answer-engine-optimization-aeo-services.tsx" },
  { path: "/linkedin-ads-agency",                     priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/linkedin-ads-agency.tsx" },
  { path: "/pinterest-ads-agency",                    priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/pinterest-ads-agency.tsx" },
  { path: "/programmatic-advertising-agency",         priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/programmatic-advertising-agency.tsx" },
  { path: "/google-local-service-ads",                priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/google-local-service-ads.tsx" },
  { path: "/pay-per-click-agency",                    priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/pay-per-click-agency.tsx" },
  { path: "/digital-advertising-consulting",          priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/digital-advertising-consulting.tsx" },
  { path: "/lead-gen-services",                       priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/lead-gen-services.tsx" },
  { path: "/ai-transformation-services",              priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/ai-transformation-services.tsx" },

  // Training
  { path: "/ai-training-for-teams",  priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/team-training.tsx" },

  // Industry
  { path: "/ecommerce",                       priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/ecommerce.tsx" },
  { path: "/home-service-lead-generation",    priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/home-service-lead-generation.tsx" },
  { path: "/industries/saas",                 priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/industries/saas.tsx" },
  { path: "/industries/automotive",           priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/industries/automotive.tsx" },
  { path: "/industries/fitness-sporting-goods", priority: 0.8, changefreq: "monthly", sourceFile: "client/src/pages/industries/fitness-sporting-goods.tsx" },

  // Standalone reports + pages
  { path: "/taylor-swift-effect-impact-on-nfl-super-bowl-lviii", priority: 0.4, changefreq: "yearly",  sourceFile: "client/src/pages/taylor-swift-effect-impact-on-nfl-super-bowl-lviii.tsx" },
  { path: "/testimonials",                                       priority: 0.7, changefreq: "weekly",  sourceFile: "client/src/pages/testimonials.tsx" },
  { path: "/measurement",                                        priority: 0.6, changefreq: "monthly", sourceFile: "client/src/pages/measurement.tsx" },

  // Content & Resources indexes
  { path: "/blog",         priority: 0.8, changefreq: "weekly",  sourceFile: "client/src/pages/blog.tsx" },
  { path: "/case-studies", priority: 0.8, changefreq: "weekly",  sourceFile: "client/src/pages/case-studies.tsx" },
  { path: "/workshops",    priority: 0.8, changefreq: "weekly",  sourceFile: "client/src/pages/events.tsx" },

  // Utility & tools
  { path: "/ai-readiness",                   priority: 0.7, changefreq: "monthly", sourceFile: "client/src/pages/ai-readiness.tsx" },
  // Code-gated instrument. The landing explains it; /report/:token is noindex and never listed.
  { path: "/ai-adoption-assessment",         priority: 0.5, changefreq: "monthly", sourceFile: "client/src/pages/ai-adoption-assessment.tsx" },
  { path: "/claude-cowork-recipe-generator", priority: 0.6, changefreq: "monthly", sourceFile: "client/src/pages/cowork-recipe-generator.tsx" },
  { path: "/terms",        priority: 0.3, changefreq: "yearly",  sourceFile: "client/src/pages/terms.tsx" },
  { path: "/privacy",      priority: 0.3, changefreq: "yearly",  sourceFile: "client/src/pages/privacy.tsx" },
];

// ─── Routable but deliberately NOT in the sitemap ────────────────────────────
// Under-construction stubs (115-word placeholders), workshop material,
// transactional confirmations, internal tools, and the /events legacy alias.
// These must stay listed here so the production 404 handler keeps serving
// them with HTTP 200.

export const NON_SITEMAP_STATIC_ROUTES: string[] = [
  // Under-construction stubs — re-add to SITEMAP_STATIC_ROUTES when built
  "/sherpa",
  "/clients",
  "/books",
  "/reading-list",
  "/tools",
  "/consulting/audit",
  "/consulting/ai",
  "/training/dolah",
  // Workshop slide decks + notes (indexability decision pending — F2)
  "/training/claude-code-workshop-slides",
  "/training/claude-code-workshop-slides-v2",
  "/training/claude-design-brief-to-creative",
  "/training/claude-design-brief-to-creative/kit",
  "/training/claude-for-design-workshop-slides",
  "/training/claude-for-design-workshop-kit",
  "/training/claude-cowork-workshop-slides",
  "/training/claude-cowork-workshop-slides/print",
  "/training/claimfox-cowork-workshop-slides",
  "/training/medisca-training-slides",
  "/training/mobilefuse-sales-slides",
  "/training/claude-dataviz-workshop-slides",
  "/training/claude-for-sales-workshop-slides",
  // Kids camp decks — emailed to enrolled families, never public
  "/training/kids-parent-orientation-slides",
  "/training/kids-camp/session-1",
  // Camper prompt page — read aloud over Zoom, never a search destination
  "/kids/tonight",
  // Transactional / internal
  "/ai-training-for-teams/thank-you",
  "/reminder/confirmed",
  // Kids camp program terms — linked from the checkout clickwrap; routable
  // but kept out of the sitemap like other transactional/legal pages
  "/claude-code-for-kids/program-terms",
  // Camper intake — emailed to enrolled parents only.
  // Routable so the link works, noindex so it is never a search destination.
  "/claude-code-for-kids/build-profile",
  // Family Demo Night RSVP — emailed to enrolled parents only, same posture.
  "/claude-code-for-kids/demo-night",
  // Showcase permission grant — emailed to enrolled parents only, same posture.
  "/claude-code-for-kids/showcase",
  // Post-camp Google review funnel — emailed to camp families. Routable so
  // the link works, noindex + out of the sitemap so it is never a search hit.
  "/claude-code-for-kids/review",
  "/design-system",
  "/design-system/symbols",
  "/nav-wireframe",
  // Lab: the three.js Impressionist Room test page. Routable so the URL
  // works, always noindex (server/page-meta.ts), never in the sitemap.
  "/lab/museum",
  // Legacy alias — Cloudflare 301s /events → /workshops at the edge, but the
  // origin still renders it for direct hits.
  "/events",
  // The AI Context Toolkit itself (moved in 2026-10-27). Account-bound and noindex;
  // /ai-context-toolkit is the page people should find.
  "/kit",
  "/kit/build",
  "/kit/improve",
  "/kit/audit",
  "/kit/method",
  // The promo-code variant of the membership page: routable, noindex, never
  // in the sitemap (server/page-meta.ts ALWAYS_NOINDEX_PREFIXES).
  "/promo",
  // Membership dashboard (client-rendered behind blank SSR shells; never in
  // the sitemap, always noindex — see MEMBER_ROUTE_PREFIXES below)
  "/membership/success",
  "/dashboard",
  "/design-builder",
  "/login",
  "/signup",
  "/claim",
  "/auth/verify",
  "/auth/magic",
  "/auth/reset",
];

const KNOWN_STATIC_PATHS: Set<string> = new Set([
  ...SITEMAP_STATIC_ROUTES.map((r) => r.path),
  ...NON_SITEMAP_STATIC_ROUTES,
]);

/** True when `path` (no query string, no trailing slash except "/") is a real static route. */
export function isKnownStaticPath(path: string): boolean {
  return KNOWN_STATIC_PATHS.has(path);
}

// ─── noindex, follow routes ───────────────────────────────────────────────────
// Paid workshop material (slide decks, session notes, attendee kits) that is
// reachable by direct URL but must not be indexed — giving the deck away in
// SERPs, and a C&D-window concern for the Anthropic-related decks. "follow"
// (not "nofollow") keeps the internal links crawlable; only the page itself
// is dropped from the index. Forced regardless of ENABLE_INDEXING.
//
// The static kit HTML (/claude-design-workshop-kit*) is served by
// express.static and never hits getPageMeta — server/static.ts sets an
// X-Robots-Tag header on those mounts. Listing the kit prefixes here keeps
// the policy documented in one place.
export const NOINDEX_FOLLOW_PREFIXES: string[] = [
  "/training/kids-parent-orientation-slides",
  "/training/kids-camp/",
  "/kids/tonight",
  // SSR slide-deck + notes routes (matched by getPageMeta + usePageMeta)
  "/training/claude-code-workshop-slides",
  "/training/claude-code-workshop-slides-v2",
  "/training/claude-design-brief-to-creative",
  "/training/claude-design-brief-to-creative/kit",
  "/training/claude-for-design-workshop-slides",
  "/training/claude-for-design-workshop-kit",
  "/training/claude-cowork-workshop-slides",
  "/training/claimfox-cowork-workshop-slides",
  "/training/medisca-training-slides",
  "/training/mobilefuse-sales-slides",
  "/training/claude-dataviz-workshop-slides",
  // Static attendee-kit HTML (matched by the X-Robots-Tag header in static.ts)
  "/claude-design-workshop-kit",
  "/claude-design-workshop-kit-june-10",
  "/claude-design-workshop-kit-official",
  // Membership surface — auth pages and the member dashboard
  "/membership/success",
  "/dashboard",
  "/design-builder",
  "/login",
  "/signup",
  "/claim",
  "/auth/",
];

/**
 * True when `path` should ship robots `noindex, follow`. Prefix match so
 * sub-routes (e.g. the /print variant of a slide deck, or any page inside a
 * kit directory) are covered without enumerating every one.
 */
export function isNoindexFollowPath(path: string): boolean {
  return NOINDEX_FOLLOW_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + "/"),
  );
}
